import React, { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  isolate?: boolean; // If true, only catches errors in this component tree
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
  errorCount: number;
  lastErrorTime: number;
}

class ErrorBoundary extends Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: '',
      errorCount: 0,
      lastErrorTime: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const now = Date.now();
    return { 
      error,
      errorId: `error-${now}-${Math.random().toString(36).substr(2, 9)}`,
      lastErrorTime: now
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const now = Date.now();
    const timeSinceLastError = now - this.state.lastErrorTime;
    
    // If errors are happening rapidly (within 1 second), increment count
    const newErrorCount = timeSinceLastError < 1000 ? this.state.errorCount + 1 : 1;
    
    console.error('Error caught by boundary:', error, errorInfo, `(${newErrorCount})`);
    
    // Only show error UI after 3 consecutive errors within a short time frame
    // This prevents brief loading errors from showing the error boundary
    const shouldShowError = newErrorCount >= 3;
    
    // Log to monitoring service in production (but not for brief loading errors)
    if (process.env.NODE_ENV === 'production' && shouldShowError) {
      this.logErrorToService(error, errorInfo);
    }
    
    this.setState({ 
      error, 
      errorInfo,
      errorCount: newErrorCount,
      hasError: shouldShowError
    });

    // Call optional error handler only for persistent errors
    if (shouldShowError && this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Auto-retry for brief errors (don't show error UI immediately)
    if (!shouldShowError) {
      // Clear any existing retry timeout
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
      }
      
      // Reset error state after a brief delay to allow for auto-recovery
      this.retryTimeout = setTimeout(() => {
        this.setState({
          hasError: false,
          error: undefined,
          errorInfo: undefined,
          errorCount: 0
        });
      }, 100);
    }
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Placeholder for error logging service
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };
    
    console.error('Error logged:', errorData);
    // In production: send to Sentry, LogRocket, or similar service
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    // Clear any pending retry timeout
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: '',
      errorCount: 0,
      lastErrorTime: 0
    });
  };

  componentWillUnmount() {
    // Cleanup timeout on unmount
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="max-w-md w-full mx-auto p-6 text-center">
            <div className="text-6xl mb-6">⚠️</div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              We apologize for the inconvenience. The page has encountered an error.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
            >
              Reload Page
            </button>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-red-500 font-medium">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 p-4 bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-300 overflow-auto rounded">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

// Specific error boundaries for different parts of the app
export const AppErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    onError={(error, errorInfo) => {
      // App-level error handling (console logging removed for production)
    }}
  >
    {children}
  </ErrorBoundary>
);

export const ComponentErrorBoundary: React.FC<{ 
  children: ReactNode; 
  componentName?: string;
  fallback?: ReactNode;
}> = ({ children, componentName = 'Component', fallback }) => (
  <ErrorBoundary
    isolate
    fallback={fallback || (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-center">
        <p className="text-red-600 font-medium">{componentName} Error</p>
        <p className="text-red-500 text-sm mt-1">This component failed to load</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-2 px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
        >
          Reload
        </button>
      </div>
    )}
    onError={(error, errorInfo) => {
      // Component error handling (console logging removed for production)
    }}
  >
    {children}
  </ErrorBoundary>
);

export const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary
    fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Page Error
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            This page encountered an error and couldn't load properly.
          </p>
          <div className="space-x-4">
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
            <a 
              href="/"
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 inline-block"
            >
              Go Home
            </a>
          </div>
        </div>
      </div>
    }
    onError={(error, errorInfo) => {
      // Route error handling (console logging removed for production)
    }}
  >
    {children}
  </ErrorBoundary>
); 